import request from "../../utils/request";
import { Result } from "../../models/general";

// ─────────────────────────────────────────────────────────────────────────────
// 🔴 OWNER DECISION E12 (2026-08-20) — business configuration has LEFT appsettings.json.
//
// Two levels, both in-app, both Operator-managed:
//   * PLATFORM DEFAULT — global, a seeded row. With the config key deleted this is the ONLY source
//     of a default, so it cannot be cleared, only set to another value.
//   * PER-COMPANY OVERRIDE — one tenant. `null` means NOT CONFIGURED and inherits the platform
//     default. Null is never "zero" and never "unlimited".
//
// 🔴 TRISTATE: render the platform value as the placeholder beside an empty field so the
// distinction is visible, and send `null` (not `""`, not `0`) to clear an override.
// ─────────────────────────────────────────────────────────────────────────────

/** `'positiveIntegerOrUnlimited' | 'monthStamp'` — a token, rendered from an i18n key, never printed raw. */
export type PlatformSettingKind = "positiveIntegerOrUnlimited" | "monthStamp";

export interface PlatformSettingDefinitionView {
  key: string;
  kind: PlatformSettingKind | string;
  labelKey: string;
  descriptionKey: string;
  /** The consequence sentence to render AT THE POINT OF EDIT. May be null. */
  consequenceKey: string | null;
  allowsCompanyOverride: boolean;
  /** What this setting behaved as before it left appsettings.json. */
  documentedDefault: string;
  /** The platform row's raw value. null ⇒ the seed did not land. */
  platformValue: string | null;
  /** 🔴 false ⇒ running on the last-ditch documented constant. Surface it, never hide it. */
  platformSeeded: boolean;
  /** What the platform level resolves to today. */
  effectiveValue: string;
  note: string | null;
  updatedOn: string | null;
  /** The literal an operator must choose to mean "no limit". Null for kinds with no unlimited state. */
  unlimitedSentinel: string | null;
}

export interface PlatformSettingCompanyView {
  companyId: number;
  companyName: string;
  /** key ⇒ raw override. null = not configured, inherits the platform default. */
  overrides: Record<string, string | null>;
  /** key ⇒ what will actually be applied for this tenant today. */
  effective: Record<string, string>;
  notes: Record<string, string | null>;
}

export interface PlatformSettingConsoleView {
  definitions: PlatformSettingDefinitionView[];
  companies: PlatformSettingCompanyView[];
}

/**
 * FULL REPLACE, not a patch.
 * - Company level: `value: null` CLEARS the override and returns the tenant to the platform default.
 * - Platform level: `value: null` is REFUSED — there is nothing left to fall back to.
 */
export interface PlatformSettingWriteInput {
  value: string | null;
  note: string | null;
  /**
   * 🔴 Required to move a month-stamp setting EARLIER than its current effective value. Without it
   * the server answers `PLATFORM_SETTING_RETROACTIVE_CONFIRM_REQUIRED`, because a backwards move
   * re-evaluates already-closed payroll months that HR cannot remediate.
   */
  acknowledgeRetroactive: boolean;
}

/** The error code the server returns when a retroactive move needs a second, deliberate act. */
export const PLATFORM_SETTING_RETROACTIVE_CONFIRM_REQUIRED = "PLATFORM_SETTING_RETROACTIVE_CONFIRM_REQUIRED";

/** Operator only. Every key, its platform default, and every tenant's override. */
export async function getPlatformSettingsConsole() {
  const response = await request.get<Result<PlatformSettingConsoleView>>("PlatformSettings/Console");
  return response.data;
}

/** Operator only. Sets the PLATFORM DEFAULT for one key. */
export async function setPlatformDefault(key: string, input: PlatformSettingWriteInput) {
  const response = await request.put<Result<PlatformSettingDefinitionView>>(
    `PlatformSettings/Default/${encodeURIComponent(key)}`,
    input,
  );
  return response.data;
}

/** Operator only. Sets or (with a null value) CLEARS one tenant's override for one key. */
export async function setCompanySettingOverride(companyId: number, key: string, input: PlatformSettingWriteInput) {
  const response = await request.put<Result<PlatformSettingCompanyView>>(
    `PlatformSettings/Company/${companyId}/${encodeURIComponent(key)}`,
    input,
  );
  return response.data;
}
